<template>
  <CustomDialog
    v-model="showModal"
    title="pack_quantity_modal_title"
    :width="'600px'"
    :footer="true"
    :close-btn="true"
    :submitText="'add_to_cart'"
    @submit="addToCart"
    @input="handleModalClose"
  >
    <template #activator>
      <div class="pack-quantity-content" v-if="product">
        <v-container fluid>
          <v-card outlined class="mb-4 rounded-lg">
            <v-card-title class="text-subtitle-1 font-weight-bold">
              <v-icon small color="primary" left>fal fa-box</v-icon>
              {{ $t("product_information") }}
            </v-card-title>
            <v-divider class="mt-0" />
            <v-card-text class="pa-4">
              <v-row>
                <v-col cols="12" sm="4" class="text-center">
                  <v-avatar tile size="100" class="product-image-container">
                    <app-img
                      :img="product.img || '/images/no_photo.jpg'"
                      width="100"
                      height="100"
                    >
                      <template v-slot:placeholder>
                        <v-row
                          class="fill-height ma-0"
                          align="center"
                          justify="center"
                        >
                          <v-icon size="48" color="grey lighten-1"
                            >fal fa-image</v-icon
                          >
                        </v-row>
                      </template>
                    </app-img>
                  </v-avatar>
                </v-col>
                <v-col cols="12" sm="8">
                  <v-row dense>
                    <v-col cols="6" class="text-caption text--secondary">{{
                      $t("product_name")
                    }}</v-col>
                    <v-col cols="6" class="text-right">
                      <span class="font-weight-medium">{{ product.name }}</span>
                    </v-col>
                    <v-col cols="6" class="text-caption text--secondary">{{
                      $t("barcode")
                    }}</v-col>
                    <v-col cols="6" class="text-right">
                      <span class="font-weight-medium">{{
                        product.code || "-"
                      }}</span>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-card outlined class="mb-4 rounded-lg">
            <v-card-title class="text-subtitle-1 font-weight-bold">
              <v-icon small color="primary" left>fal fa-cubes</v-icon>
              {{ $t("pack_quantity_modal_title") }}
            </v-card-title>
            <v-divider class="mt-0" />
            <v-card-text class="pa-4">
              <v-row>
                <v-col cols="12" sm="6">
                  <div class="text-caption text--secondary mb-1">
                    {{ $t("items_per_pack") }}
                  </div>
                  <!-- For dynamic products, make unit amount editable -->
                  <!-- Note: unitAmount has NO max constraint - user can enter any value >= 1 -->
                  <!-- Only quantity (packs) is limited by stock: Math.floor(stock / unitAmount) -->
                  <v-text-field
                    v-if="isDynamic"
                    v-model.number="unitAmount"
                    type="number"
                    :min="1"
                    outlined
                    dense
                    hide-details
                    @input="clampUnitAmount"
                  />
                  <!-- For pack products, show read-only pack amount -->
                  <div v-else class="text-h6">{{ pack }}</div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="text-caption text--secondary mb-1">
                    {{ $t("number_of_packs") }}
                    <span
                      v-if="
                        product.track_stock !== 0 &&
                        product.track_stock !== '0' &&
                        maxPacks >= 0
                      "
                      class="ml-1"
                      :class="maxPacks === 0 ? 'error--text' : ''"
                    >
                      ({{ $t("max") }}: {{ maxPacks | ToNumber }})
                    </span>
                  </div>
                  <v-text-field
                    v-model.number="qty"
                    type="number"
                    :min="maxPacks === 0 ? 0 : 1"
                    :max="maxPacks"
                    :disabled="maxPacks === 0"
                    outlined
                    dense
                    hide-details
                    @input="clampQty"
                  />
                </v-col>
              </v-row>
              <!-- Warning when unitAmount exceeds stock -->
              <v-alert
                v-if="
                  maxPacks === 0 &&
                  product.track_stock !== 0 &&
                  product.track_stock !== '0'
                "
                type="warning"
                dense
                text
                class="mt-2"
              >
                <small>
                  {{ $t("unit_amount_exceeds_stock") }}. {{ $t("max_packs") }}:
                  0
                </small>
              </v-alert>
              <div class="mt-4 text-body-2 text--secondary">
                {{ $t("pack_total_formula") }}: {{ pack }} × {{ qty }} ×
                {{ pricePerItem | NumberFormat }} =
                {{ totalPrice | NumberFormat }} {{ currencyCode }}
              </div>
            </v-card-text>
          </v-card>
        </v-container>
      </div>
    </template>
  </CustomDialog>
</template>

<script>
import CustomDialog from "@/components/customDialog.vue";
import { toast } from "@/controllers/alert";
import { myMixin } from "@/mixin/main";
import { mainStore } from "@/stores/modules/cart";

export default {
  name: "PackQuantityModal",
  components: { CustomDialog },
  mixins: [myMixin],
  props: {
    show: { type: Boolean, default: false },
    product: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      qty: 1,
      unitAmount: 1, // For dynamic products, user-entered unit amount
      loading: false,
      defineStore: mainStore(),
    };
  },
  computed: {
    showModal: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit("update:show", value);
      },
    },
    isDynamic() {
      return this.product?.size?.unit === "dynamic";
    },
    pack() {
      // For dynamic products, use user-entered unitAmount
      if (this.isDynamic) {
        return this.unitAmount;
      }
      // For pack products, use size.amount
      const a = this.product?.size?.amount;
      return a != null && a > 0 ? Number(a) : 1;
    },
    maxPacks() {
      if (
        !this.product ||
        this.product.track_stock === 0 ||
        this.product.track_stock === "0"
      ) {
        return 99999;
      }
      const s = this.product.stock || 0;
      // For dynamic products, use current unitAmount; for pack products, use pack amount
      const packSize = this.isDynamic ? this.unitAmount : this.pack;
      return packSize > 0 ? Math.floor(s / packSize) : 99999;
    },
    pricePerItem() {
      return this.product?.price_sale || 0;
    },
    currencyCode() {
      return this.product?.currency?.code || "";
    },
    totalPrice() {
      // For dynamic products, use unitAmount; for pack products, use pack
      const packSize = this.isDynamic ? this.unitAmount : this.pack;
      return packSize * this.qty * this.pricePerItem;
    },
    canAddToCart() {
      if (!this.product || !this.product.id) return false;
      // unitAmount can be any value >= 1 (no stock-based restriction)
      if (this.isDynamic && this.unitAmount < 1) return false;
      // Quantity must be > 0 and <= maxPacks
      if (this.qty < 1) return false;
      if (this.maxPacks === 0) return false; // Can't buy when max packs is 0
      if (this.product.track_stock !== 0 && this.product.track_stock !== "0") {
        // Validate total units: quantity × unitAmount <= stock
        // Note: unitAmount itself is NOT restricted by stock
        const packSize = this.isDynamic ? this.unitAmount : this.pack;
        return this.qty * packSize <= (this.product.stock || 0);
      }
      return true;
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(v) {
        if (v) {
          this.qty = 1;
          // Initialize unitAmount for dynamic products
          if (this.isDynamic) {
            // For dynamic products, start with size.amount if available, otherwise 1
            const defaultAmount = this.product?.size?.amount || 1;
            this.unitAmount = defaultAmount > 0 ? Number(defaultAmount) : 1;
          } else {
            // For pack products, reset to pack amount
            this.unitAmount = this.pack;
          }
        }
      },
    },
    // Update unitAmount when product changes
    product: {
      immediate: true,
      handler(newProduct) {
        if (newProduct && this.isDynamic) {
          const defaultAmount = newProduct?.size?.amount || 1;
          this.unitAmount = defaultAmount > 0 ? Number(defaultAmount) : 1;
        }
      },
    },
  },
  methods: {
    handleModalClose(value) {
      this.showModal = value;
      if (!value) this.closeModal();
    },
    clampQty() {
      const n = parseInt(this.qty, 10);
      if (isNaN(n) || n < 1) this.qty = 1;
      else if (n > this.maxPacks) this.qty = Math.max(1, this.maxPacks);
      else this.qty = n;
    },
    clampUnitAmount() {
      const n = parseInt(this.unitAmount, 10);
      if (isNaN(n) || n < 1) {
        this.unitAmount = 1;
      } else {
        this.unitAmount = n;
      }
    },
    closeModal() {
      this.showModal = false;
    },
    addToCart() {
      if (!this.canAddToCart) return;
      try {
        this.loading = true;
        // For dynamic products, use user-entered unitAmount; for pack products, use pack
        const finalUnitAmount = this.isDynamic ? this.unitAmount : this.pack;
        this.defineStore.addToCart({
          that: this,
          payload: this.product,
          packQty: this.qty,
          unitAmount: finalUnitAmount,
        });
        this.closeModal();
        toast(this, "add_to_cart_success");
      } catch (e) {
        console.error("PackQuantityModal addToCart", e);
        toast(this, "error_adding_to_cart", "error");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.pack-quantity-content {
  max-height: 85vh;
  overflow-y: auto;
}
.product-image-container {
  border: 1px solid #e0e0e0;
}
</style>
